import type { CommitmentsData, Locale, SectorDefinition, Theme } from '../types';
import { chrome, formatDataDate } from '../i18n';
import { PlatformBar } from '../components/PlatformBar';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProgramCard } from '../components/ProgramCard';
import { overallDelivery, programCounts, programStatus } from '../core/programs';

interface Props {
  sectors: SectorDefinition[];
  commitments: CommitmentsData;
  locale: Locale;
  theme: Theme;
}

const ORDER = { 'off-track': 0, 'at-risk': 1, 'on-track': 2 } as const;

/** Commitments lens — all national programs, risk-first. */
export function CommitmentsView({ sectors, commitments, locale, theme }: Props): string {
  const c = chrome(locale);
  const counts = programCounts(commitments.programs);
  const delivered = overallDelivery(commitments.programs);
  const sorted = [...commitments.programs].sort(
    (a, b) => ORDER[programStatus(a)] - ORDER[programStatus(b)],
  );
  const cards = sorted.map((p) => ProgramCard(p, locale, `commitments/${p.id}`)).join('');

  return `
    ${PlatformBar(sectors, 'commitments', locale, theme, true, formatDataDate(locale, commitments.updatedAt))}
    ${Breadcrumb([
      { label: c.navNational, href: 'national', icon: 'gauge' },
      { label: c.navCommitments, icon: 'flag' },
    ])}
    <section class="hero">
      <div class="hero-index">
        <div class="ring" style="--pct:${delivered}"><span class="ring-val">${delivered}<small>%</small></span><span class="ring-max">${c.delivery}</span></div>
        <div class="hero-meta">
          <div class="eyebrow">${c.commitmentsTitle}</div>
          <p class="hero-desc">${c.commitmentsSubtitle}</p>
        </div>
      </div>
      <div class="hero-tiles">
        <div class="tile"><span>${c.onTrackCount}</span><b class="ok">${counts['on-track']}</b></div>
        <div class="tile"><span>${c.atRiskCount}</span><b class="warn">${counts['at-risk']}</b></div>
        <div class="tile"><span>${c.offTrackCount}</span><b class="crit">${counts['off-track']}</b></div>
      </div>
    </section>
    <section class="grid overview-cards">${cards}</section>
    <div id="modal-root"></div>`;
}
